import React, { useState, useEffect, useCallback } from 'react';
import {
  FiZap,
  FiBookOpen,
  FiCheckCircle,
  FiAlertCircle,
  FiSettings,
  FiSave,
  FiSearch
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const AIQuestionGeneration = () => {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSyllabus, setSelectedSyllabus] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const [filters, setFilters] = useState({
    subject: 'all',
    examType: 'all',
    grade: 'all'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  
  // Question generation options
  const [generationOptions, setGenerationOptions] = useState({
    count: 5,
    difficulty: 'medium',
    topicTitle: '',
    includeExplanations: true,
    questionTypes: ['multiple_choice']
  });

  const { showError, showSuccess } = useNotification();

  const subjects = [
    { value: 'mathematics', label: 'Mathematics' },
    { value: 'english', label: 'English Language' },
    { value: 'physics', label: 'Physics' },
    { value: 'chemistry', label: 'Chemistry' },
    { value: 'biology', label: 'Biology' }
  ];

  const examTypes = [
    { value: 'waec', label: 'WAEC' },
    { value: 'jamb', label: 'JAMB' },
    { value: 'post-utme', label: 'Post-UTME' },
    { value: 'bece', label: 'BECE' }
  ];

  const grades = [
    { value: 'JSS3', label: 'JSS3' },
    { value: 'SS1', label: 'SS1' },
    { value: 'SS2', label: 'SS2' },
    { value: 'SS3', label: 'SS3' }
  ];

  const difficulties = [
    { value: 'easy', label: 'Easy', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'hard', label: 'Hard', color: 'red' }
  ];

  const fetchProcessedSyllabi = useCallback(async () => {
    try {
      setLoading(true);
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== 'all') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await adminAPI.getSyllabi({
        search: searchTerm,
        processingStatus: 'completed',
        ...cleanFilters,
        page: pagination.page,
        limit: pagination.limit
      });
      setSyllabi(response.data.data || []);
      const pag = response.data.pagination;
      setPagination(prev => ({
        ...prev,
        total: pag?.totalItems ?? pag?.total ?? 0
      }));
    } catch (error) {
      showError('Failed to load processed syllabi');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, pagination.page, pagination.limit, showError]);

  useEffect(() => {
    fetchProcessedSyllabi();
  }, [fetchProcessedSyllabi]);

  const handleGenerateQuestions = async (syllabusId) => {
    try {
      setGenerating(true);
      setSelectedSyllabus(syllabusId);
      
      console.log('🚀 Starting question generation for syllabus:', syllabusId);
      console.log('📋 Generation options:', generationOptions);
      
      const response = await adminAPI.generateQuestionsFromSyllabus(syllabusId, generationOptions);
      
      console.log('✅ API Response received:', response);
      console.log('📊 Response data:', response.data);
      console.log('❓ Questions in response:', response.data.data?.questions);
      console.log('🔢 Questions length:', response.data.data?.questions?.length);
      
      // Fix: Access questions from the nested data structure
      const questions = response.data.data?.questions || [];
      setGeneratedQuestions(questions);
      
      console.log('🎯 Setting generatedQuestions state to:', questions);
      
      setShowQuestions(true);
      showSuccess(`Generated ${questions.length} questions successfully`);
      
    } catch (error) {
      console.error('❌ Question generation error:', error);
      showError(error.response?.data?.message || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveQuestions = async () => {
    try {
      setLoading(true);
      
      await adminAPI.saveGeneratedQuestions({
        syllabusId: selectedSyllabus,
        questions: generatedQuestions
      });
      
      showSuccess(`Saved ${generatedQuestions.length} questions to question bank`);
      setShowQuestions(false);
      setGeneratedQuestions([]);
      setSelectedSyllabus(null);
      
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save questions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTopicsList = (syllabus) => {
    if (!syllabus.extractedContent?.topics) return [];
    return syllabus.extractedContent.topics.map(topic => topic.title);
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Generate questions</h1>
          <p className="text-sm text-slate-500 mt-0.5">Create questions from processed syllabi using Expert Intelligence.</p>
        </div>

        {/* Generation options bar */}
        <div className="flex flex-wrap items-center gap-4 py-3 px-4 rounded-lg bg-white border border-slate-200 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiSettings className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Options</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="sr-only">Count</label>
              <select
                value={generationOptions.count}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, count: parseInt(e.target.value) }))}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
              >
                {[1, 2, 3, 4, 5, 10, 15, 20].map(num => (
                  <option key={num} value={num}>{num} questions</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sr-only">Difficulty</label>
              <select
                value={generationOptions.difficulty}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, difficulty: e.target.value }))}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
              >
                {difficulties.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div className="w-40">
              <input
                type="text"
                value={generationOptions.topicTitle}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, topicTitle: e.target.value }))}
                placeholder="Topic (optional)"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={generationOptions.includeExplanations}
                onChange={(e) => setGenerationOptions(prev => ({ ...prev, includeExplanations: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-slate-700 focus:ring-slate-400"
              />
              <span className="text-sm text-slate-600">Include explanations</span>
            </label>
          </div>
        </div>

        {/* Search and filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              placeholder="Search syllabi..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filters.subject}
              onChange={(e) => { setFilters(prev => ({ ...prev, subject: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="all">All subjects</option>
              {subjects.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={filters.examType}
              onChange={(e) => { setFilters(prev => ({ ...prev, examType: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="all">All exam types</option>
              {examTypes.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            <select
              value={filters.grade}
              onChange={(e) => { setFilters(prev => ({ ...prev, grade: e.target.value })); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="all">All grades</option>
              {grades.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
        </div>

        {/* Syllabi table */}
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : syllabi.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiBookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-slate-800 mb-1">No processed syllabi</h3>
              <p className="text-sm text-slate-500">Process syllabi in Syllabus Management first, then generate questions here.</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Syllabus</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Subject · Exam · Grade</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Topics</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Generated</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Processed</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {syllabi.map((syllabus) => (
                    <tr key={syllabus._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <FiCheckCircle className="h-4 w-4 shrink-0 text-emerald-500" />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900">{syllabus.title}</div>
                            {syllabus.description && (
                              <div className="text-slate-500 truncate max-w-[200px]">{syllabus.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{syllabus.subject}</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="text-slate-600">{syllabus.examType} · {syllabus.grade}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {syllabus.extractedContent?.topics?.length || 0} topics
                        {getTopicsList(syllabus).length > 0 && (
                          <span className="text-slate-400 ml-1">— {getTopicsList(syllabus).slice(0, 2).join(', ')}{getTopicsList(syllabus).length > 2 ? ` +${getTopicsList(syllabus).length - 2}` : ''}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{syllabus.questionGeneration?.totalGenerated || 0}</span>
                        {syllabus.questionGeneration?.lastGeneratedAt && (
                          <span className="text-slate-400 text-xs block">Last {formatDate(syllabus.questionGeneration.lastGeneratedAt)}</span>
                        )}
                        {!syllabus.questionGeneration?.lastGeneratedAt && (
                          <span className="text-slate-400 text-xs block">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(syllabus.aiProcessing?.processedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => handleGenerateQuestions(syllabus._id)}
                          disabled={generating && selectedSyllabus === syllabus._id}
                          loading={generating && selectedSyllabus === syllabus._id}
                        >
                          <FiZap className="h-3 w-3 mr-1" />
                          Generate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Show</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                    className="px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                    aria-label="Items per page"
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span>per page</span>
                </label>
                <span className="text-sm text-slate-500">
                  {pagination.total === 0
                    ? '0 items'
                    : `${(pagination.page - 1) * pagination.limit + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}`}
                </span>
              </div>
              <Pagination
                currentPage={pagination.page}
                totalPages={Math.max(1, Math.ceil(pagination.total / pagination.limit))}
                onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
              />
            </div>
            </>
          )}
        </div>

        {/* Generated questions modal */}
        {showQuestions && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <div className="bg-white rounded-xl border border-slate-200 shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
                <h2 className="text-base font-semibold text-slate-900">Generated questions</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowQuestions(false)}>Cancel</Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveQuestions}
                    disabled={generatedQuestions.length === 0}
                  >
                    <FiSave className="h-4 w-4 mr-1.5" />
                    Save to bank
                  </Button>
                </div>
              </div>
              <div className="p-5 overflow-y-auto flex-1 min-h-0">
                {generatedQuestions.length === 0 ? (
                  <div className="text-center py-10">
                    <FiAlertCircle className="mx-auto h-10 w-10 text-slate-300 mb-3" />
                    <p className="text-sm text-slate-500">No questions generated</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {generatedQuestions.map((question, index) => (
                      <div key={index} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-800">Question {index + 1}</span>
                          <Badge variant="blue" size="sm">{question.difficulty || generationOptions.difficulty}</Badge>
                        </div>
                        <p className="text-sm text-slate-900 mb-3">{question.questionText}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                          {question.options?.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`px-3 py-2 rounded-lg border text-sm ${
                                optIndex === question.correctAnswer
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                  : 'bg-white border-slate-200 text-slate-700'
                              }`}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                        {question.explanation && (
                          <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2 mt-2">
                            <p className="text-xs font-medium text-slate-600 mb-0.5">Explanation</p>
                            <p className="text-sm text-slate-700">{question.explanation}</p>
                          </div>
                        )}
                        {question.tags && question.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {question.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIQuestionGeneration;
